import { useState } from "react"

import {
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  CloudIcon,
  GlobeAmericasIcon,
} from "@heroicons/react/24/outline"

import environmentalImpact from "~/assets/photo/solar-proposal/environmental-impact.png"
import powurCareBadge from "~/assets/photo/solar-proposal/powur-care-badge.png"

import {
  Disclosure,
  Eyebrow,
  GradText,
  Icon,
  Panel,
  Section,
  SectionHeading,
} from "../../BatteryOnlyProposal/primitives"
import { useDesktopViewport } from "../../BatteryOnlyProposal/use-desktop-viewport"
import { HomeownerApp } from "../../ProposalPresentation/HomeownerApp"
import { NextSteps } from "../../ProposalPresentation/NextSteps"
import type {
  SolarImpact,
  SolarImpactStat,
  SolarPresentationProposal,
} from "../solar-presentation"
import {
  SOLAR_CARE_BENEFITS,
  SOLAR_CARE_COVERAGE,
  SOLAR_CARE_TERMS_URL,
  SOLAR_CARE_YEARS,
  SOLAR_NEXT_STEPS,
} from "../static-copy"

function CareBenefitList(): JSX.Element {
  return (
    <>
      {SOLAR_CARE_BENEFITS.map((benefit) => (
        <div
          key={benefit.title}
          className="grid grid-cols-[22px_1fr] items-start gap-3"
        >
          <div className="mt-0.5 grid size-5 place-items-center rounded-full bg-bp-accent text-bp-on-accent">
            <Icon name="check" className="size-3" />
          </div>
          <div>
            <div className="font-display text-[length:var(--step--1)] font-semibold text-bp-text">
              {benefit.title}
            </div>
            <div className="mt-0.5 text-[length:var(--step--2)] leading-snug text-bp-text-muted">
              {benefit.note}
            </div>
          </div>
        </div>
      ))}
    </>
  )
}

function TruckRollNote(): JSX.Element {
  return (
    <p className="text-[length:var(--step--2)] leading-relaxed text-bp-text-dim">
      An initial diagnostic truck-roll fee may apply to the first service visit.
      This fee is <span className="font-semibold">fully refunded</span>
      {` `}if the reported issue is determined to be covered under this warranty.
    </p>
  )
}

/** The covered systems. One with a shorter term expands to show it, and print
 * shows every term. */
function CareCoverageList(): JSX.Element {
  const [openId, setOpenId] = useState<string | null>(null)

  return (
    <div className="flex flex-col gap-3">
      {SOLAR_CARE_COVERAGE.map((item) => {
        const icon = (
          <div className="grid size-7 place-items-center rounded-sm bg-bp-accent-soft text-bp-accent">
            <Icon name={item.icon} className="size-3.5" />
          </div>
        )
        const label = (
          <span className="font-display text-[length:var(--step--1)] font-medium text-bp-text-soft">
            {item.label}
          </span>
        )
        const { limitedTerm } = item

        if (!limitedTerm) {
          return (
            <div
              key={item.id}
              className="grid grid-cols-[32px_1fr] items-center gap-3.5 rounded-md border border-bp-border bg-bp-elevated px-3.5 py-2.5"
            >
              {icon}
              {label}
            </div>
          )
        }

        const isOpen = openId === item.id
        const detailId = `powur-care-${item.id}-term`

        return (
          <div
            key={item.id}
            className="rounded-md border border-bp-border bg-bp-elevated"
          >
            <button
              type="button"
              aria-expanded={isOpen}
              aria-controls={detailId}
              onClick={() => setOpenId(isOpen ? null : item.id)}
              className="grid w-full grid-cols-[32px_1fr_16px] items-center gap-3.5 px-3.5 py-2.5 text-left"
            >
              {icon}
              {label}
              <ChevronDownIcon
                aria-hidden="true"
                className={`size-4 text-bp-text-muted transition-transform print:hidden ${
                  isOpen ? `rotate-180` : ``
                }`}
              />
            </button>
            <p
              id={detailId}
              className={`border-t border-bp-border px-3.5 py-2.5 text-[length:var(--step--2)] leading-relaxed text-bp-text-muted ${
                isOpen ? `block` : `hidden print:block`
              }`}
            >
              {limitedTerm.before}
              <span className="font-semibold">{limitedTerm.term}</span>
              {limitedTerm.after}
            </p>
          </div>
        )
      })}
    </div>
  )
}

function CareTerms(): JSX.Element {
  return (
    <p className="mt-5 max-w-[720px] text-[length:var(--step--2)] leading-relaxed text-bp-text-dim">
      Coverage begins on Powur On day and runs continuously for{` `}
      {SOLAR_CARE_YEARS} years. See full terms at{` `}
      <a
        href={SOLAR_CARE_TERMS_URL}
        target="_blank"
        rel="noreferrer"
        className="font-semibold text-bp-accent"
      >
        powur.com/care
      </a>
      .
    </p>
  )
}

function CareHeading(): JSX.Element {
  return (
    <SectionHeading
      eyebrow="Powur Care"
      sub="Industry-leading protection on every component we install — parts, labor, and monitoring, handled by Powur for the next three decades."
    >
      Safeguard your solar with{` `}
      <GradText>{SOLAR_CARE_YEARS}-year coverage.</GradText>
    </SectionHeading>
  )
}

const BADGE_ALT = `Powur Care ${SOLAR_CARE_YEARS}-year warranty badge`

const DISCLOSURE_SUMMARY_CLASSES = `font-display text-[length:var(--step--1)] font-semibold text-bp-text`

const PILL_CLASSES = `rounded-full px-2.5 py-0.5 text-[length:var(--step--3)] font-bold tracking-wider uppercase`

function PowurCareMobile(): JSX.Element {
  return (
    <>
      <Panel className="mb-2 flex items-center gap-4 px-5 py-4">
        <img
          src={powurCareBadge}
          alt={BADGE_ALT}
          loading="lazy"
          decoding="async"
          className="h-16 w-auto object-contain"
        />
        <div>
          <div className="font-display text-[length:var(--step-0)] leading-snug font-semibold text-bp-text">
            {SOLAR_CARE_YEARS} years · $0 deductible
          </div>
          <div className="mt-0.5 text-[length:var(--step--2)] text-bp-text-muted">
            Three decades of total protection, monitored end-to-end.
          </div>
        </div>
      </Panel>
      <div className="flex flex-col gap-2">
        <Disclosure
          defaultOpen
          summary={
            <span className={DISCLOSURE_SUMMARY_CLASSES}>
              Industry-leading protection
            </span>
          }
        >
          <div className="flex flex-col gap-3.5">
            <CareBenefitList />
            <div className="border-t border-bp-border pt-3.5">
              <TruckRollNote />
            </div>
          </div>
        </Disclosure>
        <Disclosure
          summary={
            <span className={DISCLOSURE_SUMMARY_CLASSES}>
              What we cover
              <span
                className={`ml-2 bg-bp-success-soft text-bp-success ${PILL_CLASSES}`}
              >
                {SOLAR_CARE_COVERAGE.length} systems
              </span>
            </span>
          }
        >
          <CareCoverageList />
        </Disclosure>
      </div>
    </>
  )
}

/**
 * Read-only Powur Care for a design that includes it. Selecting or pricing
 * Powur Care belongs in setup, so nothing here toggles or shows a cost.
 */
export function PowurCare(): JSX.Element {
  const isDesktop = useDesktopViewport()

  if (!isDesktop) {
    return (
      <Section id="powur-care">
        <CareHeading />
        <PowurCareMobile />
        <CareTerms />
      </Section>
    )
  }

  return (
    <Section id="powur-care">
      <CareHeading />
      <div className="grid overflow-hidden rounded-[20px] border border-bp-border bg-bp-surface lg:grid-cols-3">
        <div className="relative flex flex-col items-center justify-center gap-5 overflow-hidden border-b border-bp-border px-8 py-10 lg:border-r lg:border-b-0">
          <div className="pointer-events-none absolute inset-0 bg-[image:var(--bp-product-glow)]" />
          <img
            src={powurCareBadge}
            alt={BADGE_ALT}
            loading="lazy"
            decoding="async"
            className="relative h-[240px] w-auto max-w-full object-contain drop-shadow-[0_20px_32px_rgba(0,0,0,0.55)]"
          />
          <div className="relative max-w-[260px] text-center">
            <Eyebrow className="mb-2">Coverage period</Eyebrow>
            <div className="font-display text-[length:var(--step-1)] leading-snug font-semibold tracking-[-0.015em] text-bp-text">
              Three decades of total protection, monitored end-to-end.
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-4 border-b border-bp-border px-8 py-10 lg:border-r lg:border-b-0">
          <div className="mb-1 flex items-center justify-between border-b border-bp-border pb-3.5">
            <Eyebrow>Industry-leading protection</Eyebrow>
            <div className={`bg-bp-accent-soft text-bp-accent ${PILL_CLASSES}`}>
              {SOLAR_CARE_YEARS} yrs
            </div>
          </div>
          <CareBenefitList />
          <div className="mt-auto border-t border-bp-border pt-3.5">
            <TruckRollNote />
          </div>
        </div>
        <div className="flex flex-col gap-3 px-8 py-10">
          <div className="mb-1 flex items-center justify-between border-b border-bp-border pb-3.5">
            <Eyebrow>What we cover</Eyebrow>
            <div
              className={`bg-bp-success-soft text-bp-success ${PILL_CLASSES}`}
            >
              {SOLAR_CARE_COVERAGE.length} systems
            </div>
          </div>
          <CareCoverageList />
        </div>
      </div>
      <CareTerms />
    </Section>
  )
}

/** Solar's copy for the shared "What happens next" section. */
export function SolarNextSteps(): JSX.Element {
  return (
    <NextSteps
      heading={
        <>
          From your signature to first sunbeam:{` `}
          <GradText>about 8 weeks</GradText>.
        </>
      }
      steps={SOLAR_NEXT_STEPS}
    />
  )
}

function ImpactIcon({ icon }: { icon: SolarImpactStat[`icon`] }): JSX.Element {
  if (icon === `cloud`) {
    return <CloudIcon aria-hidden="true" className="size-[22px]" />
  }
  if (icon === `leaf`) {
    return <GlobeAmericasIcon aria-hidden="true" className="size-[22px]" />
  }
  return <Icon name="home" className="size-[22px]" />
}

/** Without a home value card (TPO, or no system size), the heading drops that claim. */
export function Environment({ impact }: { impact: SolarImpact }): JSX.Element {
  return (
    <Section id="environment">
      <SectionHeading eyebrow="Environmental impact">
        Bigger than your bill: <GradText>real impact</GradText>
        {impact.claimsHomeValue ? ` and home value.` : `.`}
      </SectionHeading>
      <div className="grid gap-3.5 lg:grid-cols-[0.95fr_1fr]">
        <div className="relative min-h-[360px] overflow-hidden rounded-[20px] border border-bp-border bg-bp-elevated lg:min-h-[560px]">
          <img
            src={environmentalImpact}
            alt="Twilight rooftop with solar panels"
            loading="lazy"
            decoding="async"
            className="absolute inset-0 size-full object-cover object-bottom"
          />
          <div className="pointer-events-none absolute inset-0 bg-[image:linear-gradient(180deg,transparent_55%,rgba(0,0,0,0.7)_100%)]" />
          <div className="absolute right-7 bottom-6 left-7 text-white">
            <div className="mb-1.5 text-[11px] font-bold tracking-[0.18em] uppercase opacity-85">
              Year one
            </div>
            <div className="font-display max-w-[360px] text-[22px] leading-tight font-semibold tracking-[-0.02em]">
              Carbon-negative the moment we switch you on.
            </div>
          </div>
        </div>
        <div className="grid gap-3.5 lg:auto-rows-fr">
          {impact.stats.map((stat) => (
            <div
              key={stat.label}
              className="relative grid grid-cols-[auto_1fr] items-center gap-7 overflow-hidden rounded-[20px] border border-bp-border bg-bp-surface px-8 py-7"
            >
              <div className="pointer-events-none absolute -top-10 -right-10 size-[200px] rounded-full bg-[image:radial-gradient(circle,var(--bp-accent-soft),transparent_65%)]" />
              <div className="relative grid size-14 place-items-center rounded-[14px] border border-bp-border-strong bg-bp-elevated text-bp-accent">
                <ImpactIcon icon={stat.icon} />
              </div>
              <div className="relative">
                <div className="flex items-baseline gap-2">
                  <span className="font-display bg-[image:var(--bp-grad-accent)] bg-clip-text text-[length:var(--step-5)] leading-none font-semibold tracking-[-0.035em] text-transparent tabular-nums">
                    {stat.value}
                  </span>
                  {stat.unit ? (
                    <span className="font-display text-lg font-medium tracking-[-0.01em] text-bp-text-muted">
                      {stat.unit}
                    </span>
                  ) : null}
                </div>
                <div className="mt-2 text-[15px] leading-snug text-bp-text-soft">
                  {stat.label}
                </div>
                <div className="mt-2 max-w-[460px] text-[12.5px] leading-relaxed text-bp-text-muted">
                  {stat.detail}
                </div>
                <a
                  href={stat.source.href}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2.5 inline-flex items-center gap-1 text-[12.5px] font-semibold text-bp-accent hover:underline focus-visible:ring-2 focus-visible:ring-bp-accent focus-visible:outline-none"
                >
                  Learn more
                  <span className="sr-only">
                    {` from ${stat.source.name} (opens in a new tab)`}
                  </span>
                  <ArrowTopRightOnSquareIcon
                    aria-hidden="true"
                    className="size-3.5"
                  />
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  )
}

/** Static, so every viewer of a real proposal sees it without a read. */
export function SolarHomeownerApp(): JSX.Element {
  return (
    <HomeownerApp
      heading={
        <>
          Your entire solar experience, <GradText>simplified</GradText>.
        </>
      }
    />
  )
}

export function ClosingCta({
  proposal,
  monthlyPayment,
}: {
  proposal: SolarPresentationProposal
  monthlyPayment: string
}): JSX.Element {
  const firstName = proposal.homeowner.name.split(/\s|&/)[0]

  return (
    <section className="px-6 pb-10 lg:px-16">
      <div className="relative grid items-center gap-8 overflow-hidden rounded-[24px] bg-[image:var(--bp-grad-accent)] px-8 py-12 text-white lg:grid-cols-[1fr_auto] lg:px-14 lg:py-14">
        <div className="pointer-events-none absolute inset-0 bg-[image:radial-gradient(circle_at_80%_30%,rgba(255,255,255,0.2),transparent_50%)]" />
        <div className="relative">
          <div className="text-[11px] font-semibold tracking-[0.18em] uppercase opacity-85">
            Ready, {firstName}?
          </div>
          <h2 className="font-display mt-2 text-[length:var(--step-4)] leading-[1.05] font-semibold tracking-[-0.03em] text-white">
            Lock in {monthlyPayment} &amp;
            <br />
            start saving in 8 weeks.
          </h2>
          <p className="mt-3.5 max-w-[540px] text-[15px] opacity-90">
            Approve below and your consultant will schedule your site survey.
          </p>
        </div>
        {/* Placeholders until signing and scheduling are built; inert on purpose. */}
        <div className="relative flex min-w-[280px] flex-col gap-2.5">
          <button
            type="button"
            disabled
            className="font-display cursor-not-allowed rounded-full bg-white px-7 py-4 text-center text-[15px] font-bold text-[#0070f4] shadow-[0_12px_32px_rgba(0,0,0,0.25)]"
          >
            Sign &amp; continue →
          </button>
          <button
            type="button"
            disabled
            className="cursor-not-allowed rounded-full border border-white/40 px-7 py-3.5 text-center text-sm font-semibold text-white"
          >
            Schedule a call with {proposal.consultant.name}
          </button>
        </div>
      </div>
    </section>
  )
}
